import { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from 'hooks/hooks';
import {
    Button,
    ButtonGroup, Container
} from 'reactstrap';
import styled from "styled-components";

import { getInitialSearchDto } from "models/SearchTransactionDto";
import { setSearchDto } from "reducers/dto.slice";
import { hexToRgbA } from "services/common.service";
import { getCustomerById } from "services/customer.service";

import CreditMemos from "components/CreditMemos";
import Invoices from "components/Invoices";
import Orders from "components/Orders";
import Returns from "components/Returns";
import PdfViewer from "components/shared/PdfViewer";
import SupportCases from "components/SupportCases";
import OemAdminDash from "./OemAdminDash";

const NavButton = styled(Button)`
    background-color: var(--nav-bg) !important;
    color: var(--nav-fg) !important;
    &:hover {
        background-color: var(--nav-hover-bg) !important;
        color: var(--nav-hover-fg) !important;
        border-color: var(--nav-hover-border) !important;
    }
`;

const ActiveButton = styled(Button)`
    background-color: var(--active-bg) !important;
    color: var(--active-fg) !important;
    border-color: var(--active-border) !important;
`;


const Dashboard = () => {

    const [targetComponentName, setTargetComponentName] = useState<string>("orders");
    const customer = useAppSelector(x => x.customers.selectedCustomer);
    const customers = useAppSelector(x => x.customers.oemDropDown);
    const currentUser = useAppSelector(x => x.authentication.user);
    const selectedPdfs = useAppSelector(x => x.pdfs.selectedPdfs);
    const selectedOrder = useAppSelector(x => x.orders.selectedOrder);

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!customer && customers.length === 1 && !currentUser?.roles.includes('Administrator')) {
            dispatch(setSearchDto(getInitialSearchDto(customers[0].id)));
            dispatch(getCustomerById(customers[0].id));
        }
    }, [customers, customer, currentUser, dispatch]);

    if ((currentUser?.roles.includes('Administrator') || customers.length > 1 ) && !customer) return <OemAdminDash />;
    if (!customer) return <></>;

    const hoverBorderColor = hexToRgbA(customer.primaryColor ? (customer.primaryColor === "#ffffff" ? customer.secondaryColor || "" : customer.primaryColor) : "", .9);

    const cssVars = {
        "--nav-bg": customer.secondaryColor || "",
        "--nav-fg": customer.secondaryForeground || "",
        "--nav-hover-bg": hexToRgbA(customer.primaryColor || "", .9),
        "--nav-hover-fg": customer.primaryForeground || "",
        "--nav-hover-border": hoverBorderColor,
        "--active-bg": hexToRgbA(customer.primaryColor || ""),
        "--active-fg": customer.primaryForeground || "",
        "--active-border": hoverBorderColor,
    } as React.CSSProperties;

    const navClick = (navItem: string) => {
        dispatch(setSearchDto(getInitialSearchDto(customer!.customerId)));
        setTargetComponentName(navItem);
    }

    const renderNavButton = (name: string, display: string) => {

        if (name === targetComponentName)
            return (
                <ActiveButton
                    style={cssVars}
                    outline
                    onClick={() => navClick(name)}>
                    {display}
                </ActiveButton>);

        return (
            <NavButton
                style={cssVars}
                onClick={() => navClick(name)}>
                {display}
            </NavButton>);
    }

    const renderBody = (navSelection: string) => {
        switch (navSelection) {
            case "orders":
                return (<Orders />);
            case "invoices":
                return (<Invoices />);
            case "creditMemos":
                return (<CreditMemos />);
            case "returns":
                return (<Returns />);
            case "supportCases":
                return (<SupportCases />);
            default:
                return null;
        }
    }

    const w = window.innerWidth;

    return (
        <Container className="pt-3 mb-5 overflow-hidden" id="bcd-main-content" fluid={selectedPdfs ? true : false} >
            <hr className="my-0" />
            {selectedPdfs ?
                <PdfViewer />
                :

                <>
                    {!selectedOrder &&
                        <>
                            <ButtonGroup
                            className="d-flex justify-content-evenly  overflow-hidden"
                            size="lg"
                            vertical={w < 500}
                            >
                                {renderNavButton("orders", "Orders")}
                                {renderNavButton("invoices", "Invoices")}
                                {renderNavButton("returns", "Returns")}
                                {renderNavButton("creditMemos", "Credit Memos")}
                                {renderNavButton("supportCases", "Support Cases")}
                            </ButtonGroup>
                            <hr />
                        </>
                    }
                    {renderBody(targetComponentName)}
                </>
            }
        </Container>
    );
}

export default Dashboard;
